/**
 * VAULT · Lifestyle Moments (CMS config)
 *
 * Configuration-driven so a single component (<LifestyleVideo/>) can render
 * every human moment on the site without hard-coded asset paths.
 *
 * Fields per record (per the P0 task spec):
 *   id                  · stable identifier
 *   section             · homepage | together | family | business | documents | brief | life-moments
 *   video_title         · human-readable label
 *   video_file_desktop  · null until a real asset lands (VIDEO_ASSET_REQUIRED)
 *   video_file_mobile   · null until a real asset lands
 *   poster_desktop      · high-quality still that shows while video loads or when reduced-motion is on
 *   poster_mobile       · optional mobile-cropped still
 *   alt_description     · plain-text description for screen readers
 *   headline / sub      · copy to render alongside the moment
 *   cta                 · { label, to }
 *   active              · toggle without deleting a record
 *   display_order       · lower = earlier in the section
 *
 * `video_file_*` is intentionally `null` today — per the P0 spec we do not
 * fabricate video files. We render the poster still and mark the slot
 * VIDEO_ASSET_REQUIRED until licensed/original footage is delivered.
 */

// Sentinel placeholder — real video files land here later.
const VIDEO_ASSET_REQUIRED = null;
export { VIDEO_ASSET_REQUIRED };

export const LIFESTYLE_MOMENTS = [
    {
        id: "moment-individual-home",
        section: "homepage",
        video_title: "Individual · morning at home",
        video_file_desktop: null,  // VIDEO_ASSET_REQUIRED
        video_file_mobile:  null,  // VIDEO_ASSET_REQUIRED
        poster_desktop: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1600&q=80",
        poster_mobile:  "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=900&q=80",
        alt_description: "A professional at a home desk with a laptop, phone and a small stack of documents — starting a calm morning.",
        eyebrow: "Your day",
        headline: "Your financial life. Finally organised.",
        sub: "Less searching. Less forgetting. More clarity.",
        cta:  { label: "Create Your VAULT",       to: "/signup" },
        cta2: { label: "See how VAULT works",     to: "/product" },
        overlay: { kind: "brief", label: "VAULT Brief",     value: "Know what needs your attention." },
        active: true, display_order: 10,
    },
    {
        id: "moment-couple-together",
        section: "together",
        video_title: "Couple · organising together",
        video_file_desktop: null,  // VIDEO_ASSET_REQUIRED
        video_file_mobile:  null,  // VIDEO_ASSET_REQUIRED
        poster_desktop: "https://images.unsplash.com/photo-1573497019418-b400bb3ab074?auto=format&fit=crop&w=1600&q=80",
        poster_mobile:  "https://images.unsplash.com/photo-1573497019418-b400bb3ab074?auto=format&fit=crop&w=900&q=80",
        alt_description: "A couple at a dining table reviewing something together on a laptop — relaxed and natural.",
        eyebrow: "Household",
        headline: "Mine. Yours. Ours.",
        sub: "Organise the financial responsibilities you share without giving up individual privacy.",
        cta:  { label: "Discover VAULT Together", to: "/together" },
        overlay: { kind: "zones", label: "Zones", value: "Mine · Partner · Joint · Together" },
        active: true, display_order: 20,
    },
    {
        id: "moment-family-documents",
        section: "family",
        video_title: "Family · important documents",
        video_file_desktop: null,  // VIDEO_ASSET_REQUIRED
        video_file_mobile:  null,  // VIDEO_ASSET_REQUIRED
        poster_desktop: "https://images.unsplash.com/photo-1608889825205-eebdb9fc5806?auto=format&fit=crop&w=1600&q=80",
        poster_mobile:  "https://images.unsplash.com/photo-1608889825205-eebdb9fc5806?auto=format&fit=crop&w=900&q=80",
        alt_description: "A parent reviewing an insurance document at a home desk while everyday household life continues nearby.",
        eyebrow: "Family",
        headline: "The important things should be easy to find.",
        sub: "Policies, documents, renewals and household responsibilities deserve more than scattered folders and inbox searches.",
        cta:  { label: "Explore Family VAULT",    to: "/demo/family" },
        overlay: { kind: "renewal", label: "Renewal", value: "Home Insurance · in 18 days" },
        active: true, display_order: 30,
    },
    {
        id: "moment-business-owner",
        section: "business",
        video_title: "Business owner · one life, two worlds",
        video_file_desktop: null,  // VIDEO_ASSET_REQUIRED
        video_file_mobile:  null,  // VIDEO_ASSET_REQUIRED
        poster_desktop: "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1600&q=80",
        poster_mobile:  "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=900&q=80",
        alt_description: "A founder moving between a laptop, a phone and a small stack of papers in a natural working setup.",
        eyebrow: "Business owner",
        headline: "One life. More than one financial world.",
        sub: "Keep personal and business financial organisation clearer without mixing everything together.",
        cta:  { label: "VAULT for Business Owners", to: "/product" },
        overlay: { kind: "brief", label: "Today", value: "Personal · Business" },
        active: true, display_order: 40,
    },

    /* ─── FOUNDER STORY ───────────────────────────────────────────────
       Slot reserved for a genuine Founder-shot piece. Until real Founder
       footage is supplied, we render the poster placeholder only — we
       DO NOT generate a fake Founder avatar. */
    {
        id: "moment-founder-story",
        section: "founder",
        video_title: "Why I built VAULT — Founder",
        video_file_desktop: null,  // VIDEO_ASSET_REQUIRED (genuine Founder only)
        video_file_mobile:  null,  // VIDEO_ASSET_REQUIRED (genuine Founder only)
        poster_desktop: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80",
        poster_mobile:  "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80",
        alt_description: "A quiet home office at dusk — a laptop, a warm lamp, a small stack of papers. Placeholder for the Founder story.",
        eyebrow: "Founder story",
        headline: "I built VAULT because, at some point, my financial life became too much to keep in my head.",
        sub: "Over the years, life became more complex — more cards, bank accounts, investments, subscriptions, insurance, documents, assets, liabilities and responsibilities. Nothing was really missing. The information was there. It was just everywhere. I realised I didn't need another budgeting app. I needed one place I could trust to bring my entire financial life together — clearly, simply and in a way that made sense to me. Most of us don't need more financial apps. We need less financial clutter. — PD, Founder, VAULT by PD",
        cta:  null,
        overlay: null,
        active: true, display_order: 50,
    },

    /* ─── MINI STORIES — five moments from one financial life ─────────
       Recurring South-Asian couple / home. Same warm evening + natural
       daylight palette across the set. Each card carries an explicit
       `route` + `view` and a per-card analytics event so no two cards
       can accidentally resolve to the same generic destination. */
    {
        id: "mini-cards",
        section: "mini-stories",
        poster_desktop: "https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/2vvj41wn_vault_cards_approved.png",
        poster_mobile:  "https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/2vvj41wn_vault_cards_approved.png",
        alt_description: "A person holding a black VAULT-branded card with a phone and a small leather wallet in a warm home setting.",
        eyebrow: "Cards", headline: "One card is easy. Ten deserve a system.",
        cta: { label: "See Cards in action", route: "/demo/personal", view: "cards" },
        analytics_event: "small_moment_cards_clicked",
        active: true, display_order: 60,
    },
    {
        id: "mini-subscriptions",
        section: "mini-stories",
        poster_desktop: "https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/85ijmq3v_vault_subscriptions_approved.png",
        poster_mobile:  "https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/85ijmq3v_vault_subscriptions_approved.png",
        alt_description: "A couple at home smiling while reviewing recurring subscriptions on a laptop.",
        eyebrow: "Subscriptions", headline: "Wait — what am I still paying for?",
        cta: { label: "See Subscriptions in action", route: "/demo/personal", view: "subscriptions" },
        analytics_event: "small_moment_subscriptions_clicked",
        active: true, display_order: 61,
    },
    {
        id: "mini-documents",
        section: "mini-stories",
        poster_desktop: "https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/6y721cnq_vault_documents_approved.png",
        poster_mobile:  "https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/6y721cnq_vault_documents_approved.png",
        alt_description: "A navy VAULT-branded journal, small notebook and iPad neatly arranged on a marble desk with gold accents.",
        eyebrow: "Documents", headline: "I know that policy is somewhere…",
        cta: { label: "See Documents in action", route: "/demo/personal", view: "documents" },
        analytics_event: "small_moment_documents_clicked",
        active: true, display_order: 62,
    },
    {
        id: "mini-together",
        section: "mini-stories",
        poster_desktop: "https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/2m33i0ji_vault_together_approved.png",
        poster_mobile:  "https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/2m33i0ji_vault_together_approved.png",
        alt_description: "A couple sitting close on a sofa reviewing something together on an iPad in a warm, softly lit living room.",
        eyebrow: "Together", headline: "This one's mine. That one's yours. This one's ours.",
        cta: { label: "Explore VAULT Together", route: "/demo/couple", view: "together" },
        analytics_event: "small_moment_together_clicked",
        active: true, display_order: 63,
    },
    {
        id: "mini-business",
        section: "mini-stories",
        poster_desktop: "https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/15rswuyt_vault_business_owner_approved.png",
        poster_mobile:  "https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/15rswuyt_vault_business_owner_approved.png",
        alt_description: "A business owner at a warm home-office desk writing in a notebook next to a laptop and VAULT-branded mug.",
        eyebrow: "Business owner", headline: "Personal here. Business there. One life managing both.",
        cta: { label: "VAULT for Business Owners", route: "/solutions/business-owners" },
        analytics_event: "small_moment_business_clicked",
        active: true, display_order: 64,
    },

    /* ─── CHOOSE YOUR VAULT — persona picker cards (still-only tiles) ─ */
    {
        id: "choose-just-me",
        section: "choose",
        video_title: "Just me",
        video_file_desktop: null, video_file_mobile: null,
        poster_desktop: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=900&q=80",
        poster_mobile:  "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=600&q=80",
        alt_description: "A person at a home desk reviewing their calendar and finances.",
        eyebrow: "Just me",
        headline: "My money. My life. One clear place.",
        sub: "Everything I need to stay organised — without keeping it all in my head.",
        cta: { label: "Explore Personal VAULT", to: "/demo/personal" },
        active: true, display_order: 70,
    },
    {
        id: "choose-us",
        section: "choose",
        video_title: "Us",
        video_file_desktop: null, video_file_mobile: null,
        // Two people together — man + woman side-by-side reviewing a document
        // on a laptop. Communicates partnership / shared household finances.
        poster_desktop: "https://images.unsplash.com/photo-1611095564985-89765398121e?auto=format&fit=crop&w=900&q=80",
        poster_mobile:  "https://images.unsplash.com/photo-1611095564985-89765398121e?auto=format&fit=crop&w=600&q=80",
        alt_description: "A couple sitting side by side at home reviewing a document together on a laptop.",
        eyebrow: "Us",
        headline: "Mine. Yours. Ours.",
        sub: "Share what matters. Keep what's personal, personal.",
        cta: { label: "Explore VAULT Together", to: "/together" },
        active: true, display_order: 71,
    },
    {
        id: "choose-family",
        section: "choose",
        video_title: "Our family",
        video_file_desktop: null, video_file_mobile: null,
        // Family unit at home — clearly reads as a household with children.
        poster_desktop: "https://images.unsplash.com/photo-1581952976147-5a2d15560349?auto=format&fit=crop&w=900&q=80",
        poster_mobile:  "https://images.unsplash.com/photo-1581952976147-5a2d15560349?auto=format&fit=crop&w=600&q=80",
        alt_description: "A family together at home — parents with their children in a warm, relaxed household setting.",
        eyebrow: "Our family",
        headline: "One family. Many responsibilities. One organised place.",
        sub: "Documents, payments, renewals and shared responsibilities — easier to manage together.",
        cta: { label: "Explore Family VAULT", to: "/demo/family" },
        active: true, display_order: 72,
    },
    {
        id: "choose-business",
        section: "choose",
        video_title: "My business + me",
        video_file_desktop: null, video_file_mobile: null,
        // One founder visible at a home desk with a laptop — communicates a
        // single person managing both personal and business responsibilities.
        poster_desktop: "https://images.unsplash.com/photo-1759593218431-6f1585bc14de?auto=format&fit=crop&w=900&q=80",
        poster_mobile:  "https://images.unsplash.com/photo-1759593218431-6f1585bc14de?auto=format&fit=crop&w=600&q=80",
        alt_description: "A founder at a home-office desk working on a laptop — one person balancing personal and business responsibilities.",
        eyebrow: "My business + me",
        headline: "One life. Two financial worlds. Clearly separated.",
        sub: "Keep personal and business information organised without mixing the two.",
        cta: { label: "Explore Business VAULT", to: "/solutions/business-owners" },
        active: true, display_order: 73,
    },

    /* ─── FINAL HUMAN CLOSING CTA ───────────────────────────────────── */
    {
        id: "moment-final-cta",
        section: "final-cta",
        video_title: "Closing · time for everything else",
        video_file_desktop: null,  // VIDEO_ASSET_REQUIRED
        video_file_mobile:  null,  // VIDEO_ASSET_REQUIRED
        poster_desktop: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=1600&q=80",
        poster_mobile:  "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=900&q=80",
        alt_description: "A person closing a laptop at sunset — day ending, calm.",
        eyebrow: "Time back",
        headline: "Less searching. Less forgetting. More time for everything else.",
        sub: "Your financial life. Organised.",
        cta:  { label: "Create Your VAULT",  to: "/signup" },
        cta2: { label: "Explore Demo",       to: "/demo" },
        active: true, display_order: 90,
    },
];

/** Return the (first active) moment for a section, or null. */
export function getMoment(section) {
    return LIFESTYLE_MOMENTS.find((m) => m.section === section && m.active) || null;
}

export function listMoments(section) {
    return LIFESTYLE_MOMENTS
        .filter((m) => (!section || m.section === section) && m.active)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
}
